import { type LokiFields, postLokiRecord } from './loki-transport';

/** Loki log keys for the gift-cards flows. */
export const GiftCardsLokiKey = {
	/** A present native app-data/auth bridge failed or timed out. */
	NATIVE_SYNC_FAILED: 'GC_NATIVE_SYNC_FAILED',
	/** Initial details load or post-activation reconciliation failed. */
	DETAILS_FETCH_FAILED: 'GC_DETAILS_FETCH_FAILED',
	/** Manual voucher recharge failed in the add flow. */
	ADD_FAILED: 'GC_ADD_FAILED',
	/** Listed voucher recharge failed from a tile or the detail footer. */
	ACTIVATION_FAILED: 'GC_ACTIVATION_FAILED',
	/**
	 * A present native result bridge threw or iOS rejected delivery, so the
	 * native shell may not have learned the result. Deliberately
	 * distinct from a *missing* bridge, which is an expected older-build path
	 * (add-flow contracts §3.3) and not logged: a throwing bridge is a defect,
	 * and without this it is indistinguishable from success from our side — the
	 * only symptom is a balance that never updates, reported as a BE bug.
	 */
	BRIDGE_RESULT_FAILED: 'GC_BRIDGE_RESULT_FAILED',
	/** A gift-card route failed while loading or rendering. */
	ROUTE_ERROR: 'GC_ROUTE_ERROR',
} as const;

type GiftCardsLokiKey =
	(typeof GiftCardsLokiKey)[keyof typeof GiftCardsLokiKey];

/**
 * Error-level gift-cards log to `/logs/ui`. This domain intentionally exposes
 * no info-level helper: successful loads, initiation, success, validation, and
 * expected legacy fallbacks do not belong in the UI error stream.
 * Fire-and-forget — never throws or blocks the caller.
 */
export function logGiftCardsLokiError(
	key: GiftCardsLokiKey,
	error: unknown,
	fields?: LokiFields,
): void {
	const message =
		error instanceof Error ? error.message : String(error ?? 'unknown');
	postLokiRecord('error', key, { message, ...fields });
}
